import React, { useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { PropTypes } from 'prop-types';
import { useSnackbar } from 'notistack';

import {
  Box,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  Switch,
  Checkbox,
  Chip,
  IconButton,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Grid from '@mui/material/Grid2';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { routes } from 'config/index';
import Loading from 'components/common/loading';
import PageHeader from 'components/common/pageHeader';
import { getMessage } from 'helpers/helper';
import WineListsService from 'services/wineListsService';
import PartnersService from 'services/partnersService';
import wineListsStore from 'store/wineListsStore';
import domainData from 'store/domainStore';
import SidebarStore from 'store/ui/sidebarStore';
import QRCodeStore from 'store/QRCodeStore';
import formClasses from 'components/common/form/styles';
import tableOrWineNumberOptions from 'constants/table-or-wine-number-options';
import M from 'messages';
import DraggableTable from './menuWines';
import ColorPicker from 'components/common/form/colorPicker';
import WineListImageInput from 'components/common/form/wineListImageInput';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';

import classes from './styles';
import QRCodeModal from 'components/common/QRCode';

const WineListCreateWrapper = observer(({ isEdit, isCreate }) => {
  const currentSP = domainData.getCurrentSP();
  const isUserSuperAdmin = domainData.userIsSuperAdmin();
  const wineListStateMode = isEdit ? 'edit' : isCreate ? 'create' : '';
  wineListsStore.setWineListState(wineListStateMode);
  return (
    <WineListCreate isEdit={isEdit} isCreate={isCreate} currentSP={currentSP} isUserSuperAdmin={isUserSuperAdmin} />
  );
});

const WineListCreate = observer(({ isEdit = false, isCreate = false, currentSP, isUserSuperAdmin }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [spLisLoading, setSpLisLoading] = useState(false);
  const [currMenuData, setCurrMenuData] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [setAllTableOrWineNumber, setSetAllTableOrWineNumber] = useState('');
  const [tableOrWineNumberAll, setTableOrWineNumberAll] = useState('');
  const [isOpenQR, setIsOpenQR] = useState(false);
  const [createdMenuId, setCreatedMenuId] = useState('');

  const isSidebarOpened = SidebarStore.sidebarState;

  const data = wineListsStore.getStoreWineListForCreate();
  const dataErrors = wineListsStore.getWineListDataErrors();
  const selectedItems = wineListsStore.getSelectedWines;
  const defaultsCount = wineListsStore.getDefaultsCount();
  const allSelected = selectedItems.length > 0 && selectedItems.length === wineListsStore.getWinesDataForSp().length;

  let readonly = false;
  let disabled = false;
  if (!isUserSuperAdmin && !isCreate && !data.active) {
    disabled = true;
    readonly = true;
  }

  const getWineList = useCallback(
    async menuId => {
      try {
        setLoading(true);
        const response = await WineListsService.getWineList(menuId || id);
        if (response) {
          wineListsStore.setWineListData(response.data);
          setCurrMenuData(response.data);
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  const getSpList = useCallback(
    async () => {
      try {
        setSpLisLoading(true);
        if (isUserSuperAdmin) {
          const response = await domainData.getSPListNames();
          if (response) {
            wineListsStore.setSpList(response);
          }
        } else {
          const response = await PartnersService.getPartner(currentSP);
          if (response) {
            wineListsStore.setSpList([response.data]);
          }
        }
        setSpLisLoading(false);
      } catch (err) {
        setSpLisLoading(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  useEffect(() => {
    if (isCreate) {
      setLoading(false);
      getSpList();
      // For non-super admin users, inherit profile logo immediately
      if (!isUserSuperAdmin && currentSP) {
        inheritProfileLogo();
      }
    } else {
      getWineList();
    }
    return () => {
      if (location.pathname !== `/menus/${id}`) {
        wineListsStore.clearSpWinesData();
      }
      wineListsStore.clearWineListDataErrors();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isCreate && data.serviceProviderId) {
      wineListsStore.getSpWinesData(data.serviceProviderId);
      wineListsStore.getSpDefaultWineListsCount(data.serviceProviderId);

      // Automatically inherit profile logo if customLogo is not set
      if (!data.customLogo) {
        inheritProfileLogo();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [+data.serviceProviderId]);

  const inheritProfileLogo = useCallback(async () => {
    try {
      if (data.serviceProviderId || currentSP) {
        const spId = data.serviceProviderId || currentSP;
        const response = await PartnersService.getPartner(spId);
        if (response?.data?.logo) {
          wineListsStore.updateData({ customLogo: response.data.logo });
        }
      }
    } catch (err) {
      // Silently fail - this is just for convenience, not critical
      console.warn('Failed to inherit profile logo:', err);
    }
  }, [data.serviceProviderId, currentSP]);

  const onSubmit = async event => {
    event.preventDefault();
    const formHasError = wineListsStore.wineListDataHasError();
    if (!formHasError) {
      try {
        const cleanData = wineListsStore.getWineListData(isEdit);
        setSubmitLoading(true);
        if (isEdit) {
          await WineListsService.updateWineList(cleanData, id || createdMenuId);
          navigate(routes.wineLists.path);
        } else {
          const response = await WineListsService.createWineList(cleanData);
          if (response) {
            QRCodeStore.setQRData(response.data.qrCode, 'menuId');
            setCreatedMenuId(response.data.qrCode.menuId);
            setIsOpenQR(true);
          }
        }
      } catch (err) {
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
      } finally {
        setLoading(false);
        setSubmitLoading(false);
      }
      return true;
    }
    enqueueSnackbar(M.get('form.errors.notValidForm'), { variant: 'error' });
    return false;
  };

  const isSPDisabled = () => {
    if (!isCreate) {
      return true;
    }
    if (currentSP) {
      return true;
    }
    return false;
  };

  const getOptionSelected = (option, anotherOption) => option.id === anotherOption.id;

  const filter = createFilterOptions({
    stringify: option => `${option.name || ''} ${option.producer || ''}`,
  });

  const getOptionLabel = option => `${option.name}`;

  const getWineMeta = option => {
    const yearValue = option.year == null ? '' : String(option.year).trim();
    const year = yearValue || 'NV';
    const meta = [option.producer, option.wineCountry, option.region, year].filter(Boolean);
    return meta.join(' • ');
  };

  const getWineChipMeta = option =>
    [option.producer, option.wineCountry, option.region, option.year].filter(Boolean).join(' • ');

  const getWineChipLabel = option => option.name;

  const optionRenderer = (props, option, { selected }) => {
    if (option.id === 'select-all') {
      return (
        <Box {...props} key={option.id}>
          <Checkbox
            color="primary"
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            style={{ marginRight: 8 }}
            checked={allSelected}
          />
          {getOptionLabel(option)}
        </Box>
      );
    }
    const wineMeta = getWineMeta(option);
    return (
      <Box {...props} key={option.id} sx={classes.wineOptionRow}>
        <Checkbox
          color="primary"
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          style={{ marginRight: 8 }}
          checked={selected}
        />
        <Box sx={classes.wineOptionText}>
          <Typography component="span" sx={classes.wineOptionName}>
            {getOptionLabel(option)}
          </Typography>
          <Typography component="span" sx={classes.wineOptionMeta}>
            {wineMeta}
          </Typography>
        </Box>
      </Box>
    );
  };

  const inputRenderer = params => (
    <TextField
      {...params}
      label={M.get('wineLists.itemsPlaceholder')}
      placeholder={M.get('wineLists.itemsPlaceholder')}
    />
  );

  const handleDiscard = () => {
    navigate(routes.wineLists.path);
  };

  const handleOpenModalQR = async () => {
    try {
      setSubmitLoading(true);
      const response = await WineListsService.getQRCode(id || createdMenuId);
      QRCodeStore.setQRData(response.data, 'menuId');
      setIsOpenQR(true);
    } catch (error) {
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), { variant: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSaveQR = async () => {
    try {
      // Update the existing QR code
      await QRCodeStore.updateQRCode(WineListsService.updateQRCode);
      enqueueSnackbar('QR code updated successfully', { variant: 'success' });
      setIsOpenQR(false);
      navigate(routes.wineLists.path);
    } catch (error) {
      const errorMessage = getMessage(error?.response?.data, 'error') || 'An error occurred while saving QR code';
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
    }
  };

  const handleCloseQR = () => {
    setIsOpenQR(false);
    if (isCreate) {
      getWineList(createdMenuId);
      navigate(routes.wineListsEdit.path.replace(':id', createdMenuId));
    }
  };

  if (
    loading ||
    (!isCreate && !wineListsStore.isSpWinesDataLoaded() && !wineListsStore.isSpDefaultWineListsCountLoaded()) ||
    !selectedItems
  )
    return <Loading />;

  return (
    <>
      <QRCodeModal
        open={isOpenQR}
        handleClose={handleCloseQR}
        handleSave={handleSaveQR}
        dialogTitle={M.get('wineLists.qr.digitalListQrCode')}
        dialogDescription={M.get('wineLists.qr.description')}
        dialogLinkTitle={M.get('wineLists.qr.digitalListLink')}
      />
      <Box
        sx={{
          ...classes.page,
          ...(isSidebarOpened && classes.rootDrawerOpen),
          ...(!isSidebarOpened && classes.rootDrawerClose),
        }}>
        <PageHeader
          generalPageTitle={`${M.get('wineLists.pageHeader')} | ${isCreate ? M.get('actions.create') : currMenuData?.menuName || ''}`}
          onlyGeneralPageTitle
        />
        {/* Content */}
        <Box sx={classes.pageContent}>
          <form noValidate>
            <Box component={Paper} sx={classes.boxContainer} pb={2} px={3} pt={3}>
              <Typography variant="h6" sx={classes.sectionTitle} style={{ marginBottom: '16px' }}>
                {`${M.get('wineLists.publicList')}`}
              </Typography>
              <Grid container spacing={3}>
                {isUserSuperAdmin && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth variant="outlined" sx={classes.selectHeight}>
                      <InputLabel id="sp-select-label" sx={dataErrors.serviceProviderId && formClasses.invalidSelect}>
                        {`${M.get('wineLists.serviceProvider')} *`}
                      </InputLabel>
                      <Select
                        id="sp-select"
                        name="serviceProviderId"
                        labelId="sp-select-label"
                        label={`${M.get('wineLists.serviceProvider')} *`}
                        disabled={disabled || isSPDisabled()}
                        value={data.serviceProviderId ?? ''}
                        defaultValue={data.serviceProviderId}
                        onChange={wineListsStore.onChangeField}
                        required
                        onMouseDown={event => {
                          event.stopPropagation();
                        }}
                        sx={{
                          ...classes.select,
                          ...(dataErrors.serviceProviderId && classes.invalidSelectContainer),
                        }}
                        MenuProps={{
                          classes: { paper: classes.menuPaper },
                          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                        }}>
                        {spLisLoading ? (
                          <MenuItem value={data.serviceProviderId} disabled>
                            {M.get('wineLists.loading')}
                          </MenuItem>
                        ) : isCreate ? (
                          wineListsStore.getSpList?.map(opt => (
                            <MenuItem key={opt.id || opt.value} value={opt.id || opt.value}>
                              {opt.name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value={data.serviceProviderId}>{data.serviceProvider?.name}</MenuItem>
                        )}
                      </Select>
                      <FormHelperText sx={classes.errorHelperText}>
                        {' '}
                        {dataErrors.serviceProviderId && M.get('form.errors.required')}{' '}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      type="text"
                      label={M.get('wineLists.menuTitle')}
                      id="menuName"
                      name="menuName"
                      value={data.menuName}
                      disabled={disabled}
                      required
                      onChange={wineListsStore.onChangeField}
                      variant="outlined"
                      error={!!dataErrors.menuName}
                      inputProps={{ maxLength: 255 }}
                      helperText={
                        dataErrors.menuName ? (
                          M.get('form.errors.required')
                        ) : (
                          <Box component="span" sx={classes.helperDiv}>
                            <Box component="span">{M.get('wineLists.menuItemHelper')}</Box>
                            <Box component="span">
                              {data?.menuName?.length || 0}/{wineListsStore.getMaxWineListNameSymbols()}
                            </Box>
                          </Box>
                        )
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid mt={3} size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth variant="outlined" sx={classes.formControl}>
                    <TextField
                      label={`${M.get('signUp.description')}`}
                      placeholder={M.get('wineLists.descriptionPlaceholder')}
                      id="description"
                      name="description"
                      value={data.description}
                      onChange={wineListsStore.onChangeField}
                      multiline
                      rows="6"
                      size="medium"
                      required
                      error={!!dataErrors.description}
                      helperText={dataErrors.description && M.get('form.errors.required')}
                      disabled={disabled}
                      slotProps={{
                        input: {
                          sx: { position: 'relative', paddingRight: '40px' },
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{
                                position: 'absolute',
                                top: '12px',
                                right: '12px',
                              }}>
                              <Tooltip placement="top" title={M.get('wineLists.descriptionTooltip')}>
                                <HelpOutlineIcon sx={classes.fieldHeaderIcon} />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {/* Wine List Personalization Section */}
              <Grid mt={3} size={{ xs: 12 }}>
                <Box sx={classes.personalizationSection}>
                  <Typography variant="h6" sx={classes.sectionTitle} style={{ marginBottom: '0px' }}>
                    {M.get('wineLists.personalization.title')}
                    {!domainData.getSubscriptionAccess('dashboard') && (
                      <Tooltip
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': classes.tooltip,
                          },
                        }}
                        title={
                          <Typography sx={classes.noAccess}>
                            {M.get('subscriptions.noAccess1')}
                            &nbsp;
                            <a
                              style={classes.link}
                              href={`${routes.subscriptions.path}?type=standard`}
                              rel="noopener noreferrer">
                              {M.get('subscriptions.upgrade')}
                            </a>
                            &nbsp;
                            {M.get('subscriptions.noAccess2')}
                          </Typography>
                        }
                        placement="top">
                        <LockIcon sx={classes.lockIcon} />
                      </Tooltip>
                    )}
                  </Typography>
                  <Typography variant="body2" component="p" style={{ fontStyle: 'italic', marginBottom: '16px' }}>
                    {M.get('wineLists.personalization.subtitle')}
                  </Typography>

                  <Grid container spacing={3} mt={3}>
                    {/* Custom Logo */}
                    <Grid size={{ xs: 12, sm: 12 }}>
                      <WineListImageInput
                        label={M.get('wineLists.personalization.customLogo')}
                        value={data.customLogo || ''}
                        onChange={value => wineListsStore.updateData({ customLogo: value })}
                        disabled={disabled || !domainData.getSubscriptionAccess('dashboard')}
                        tooltip={M.get('wineLists.personalization.customLogoTooltip')}
                        imageType="logo"
                      />
                    </Grid>

                    {/* Header Background Color */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      {/* make input and reset in same row */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ColorPicker
                          label={M.get('wineLists.personalization.headerBackgroundColor')}
                          value={
                            data.headerBackgroundColor ||
                            'linear-gradient(90deg, rgb(135, 41, 44), rgb(217, 56, 56) 50%, rgb(223, 28, 40))'
                          }
                          onChange={value => wineListsStore.updateData({ headerBackgroundColor: value })}
                          disabled={disabled || !domainData.getSubscriptionAccess('dashboard')}
                          helperText={
                            !domainData.getSubscriptionAccess('dashboard') ? (
                              <>
                                {M.get('subscriptions.shortNoAccess1')}
                                &nbsp;
                                <a
                                  style={classes.redLink}
                                  href={`${routes.subscriptions.path}?type=standard`}
                                  rel="noopener noreferrer">
                                  {M.get('subscriptions.upgrade')}
                                </a>
                                &nbsp;
                                {M.get('subscriptions.shortNoAccess2')}
                              </>
                            ) : (
                              M.get('wineLists.personalization.headerBackgroundColorTooltip')
                            )
                          }
                          error={!domainData.getSubscriptionAccess('dashboard')}
                          allowGradient={true}
                        />
                        {/* maybe use icon with tooltip to reset the color */}
                        <Tooltip title={M.get('wineLists.personalization.resetHeaderBackgroundColor')}>
                          <IconButton
                            onClick={() =>
                              wineListsStore.updateData({
                                headerBackgroundColor:
                                  'linear-gradient(90deg, rgb(135, 41, 44), rgb(217, 56, 56) 50%, rgb(223, 28, 40))',
                              })
                            }
                            disabled={disabled}>
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>

                    {/* Header Font Color */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ColorPicker
                          label={M.get('wineLists.personalization.headerFontColor')}
                          value={data.headerFontColor || '#ffffff'}
                          onChange={value => wineListsStore.updateData({ headerFontColor: value })}
                          disabled={disabled || !domainData.getSubscriptionAccess('dashboard')}
                          helperText={
                            !domainData.getSubscriptionAccess('dashboard') ? (
                              <>
                                {M.get('subscriptions.shortNoAccess1')}
                                &nbsp;
                                <a
                                  style={classes.redLink}
                                  href={`${routes.subscriptions.path}?type=standard`}
                                  rel="noopener noreferrer">
                                  {M.get('subscriptions.upgrade')}
                                </a>
                                &nbsp;
                                {M.get('subscriptions.shortNoAccess2')}
                              </>
                            ) : (
                              M.get('wineLists.personalization.headerFontColorTooltip')
                            )
                          }
                          error={!domainData.getSubscriptionAccess('dashboard')}
                          allowGradient={true}
                        />
                        <Tooltip title={M.get('wineLists.personalization.resetHeaderFontColor')}>
                          <IconButton
                            onClick={() => wineListsStore.updateData({ headerFontColor: '#ffffff' })}
                            disabled={disabled}>
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              {!data.isDefault && defaultsCount > 0 && (
                <Grid mt={3} size={{ xs: 12, sm: 6 }}>
                  <FormControl>
                    <FormControlLabel
                      sx={classes.checkboxLabel}
                      disabled={disabled}
                      control={
                        <Switch
                          name="shouldResetWineRating"
                          checked={!!data.shouldResetWineRating}
                          onChange={wineListsStore.onChangeCheckBox}
                          color="primary"
                        />
                      }
                      labelPlacement="start"
                      label={
                        <Typography sx={classes.fieldHeaderText}>
                          {M.get('wineLists.shouldResetWineRating')}{' '}
                          <Tooltip title={M.get('wineLists.shouldResetWineRatingTooltip')}>
                            <HelpOutlineIcon sx={classes.fieldHeaderIcon} />
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </FormControl>
                </Grid>
              )}
              <Divider sx={classes.divider} />
              <Grid container spacing={2} sx={classes.menuItemsArea}>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ ...classes.fieldHeader }}>
                    <Typography component="h6" sx={classes.fieldHeaderText}>
                      {M.get('wineLists.items')}{' '}
                      <Tooltip title={M.get('wineLists.winesTooltip')} placement="right">
                        <HelpOutlineIcon sx={classes.fieldHeaderIcon} />
                      </Tooltip>
                    </Typography>
                  </Box>
                  <Typography variant="body2" component="div" style={{ fontStyle: 'italic', marginBottom: '16px' }}>
                    {M.get('wineLists.itemsDescription')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth variant="outlined">
                    <Autocomplete
                      multiple
                      limitTags={5}
                      options={wineListsStore.getWinesDataForSp()}
                      value={wineListsStore.getSelectedWines}
                      disableCloseOnSelect
                      getOptionLabel={getOptionLabel}
                      getOptionSelected={getOptionSelected}
                      noOptionsText={M.get('wineLists.noOptions')}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);
                        if (filtered.length) {
                          filtered.unshift({ name: 'Select All', id: 'select-all' });
                        }
                        return filtered;
                      }}
                      onChange={wineListsStore.onChangeItemsField}
                      renderOption={optionRenderer}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={inputRenderer}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            size="small"
                            sx={classes.wineChip}
                            label={
                              <Box sx={classes.wineChipLabel}>
                                <Typography component="span" sx={classes.wineChipName}>
                                  {getWineChipLabel(option)}
                                </Typography>
                                {!!getWineChipMeta(option) && (
                                  <Typography component="span" sx={classes.wineChipMeta}>
                                    {getWineChipMeta(option)}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        ))
                      }
                      getOptionDisabled={option => option.id !== 'select-all' && !option.active}
                      disabled={!data.serviceProviderId || disabled}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ md: 12, lg: 5 }} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControl>
                    <FormControlLabel
                      sx={classes.checkboxLabel}
                      disabled={disabled}
                      control={
                        <Switch
                          name="showTableOrWineNumber"
                          checked={data.showTableOrWineNumber}
                          onChange={e =>
                            wineListsStore.onChangeField({
                              target: { value: e.target.checked, name: 'showTableOrWineNumber' },
                            })
                          }
                          color="primary"
                          disabled={disabled}
                        />
                      }
                      labelPlacement="start"
                      label={
                        <Typography sx={{ ...classes.fieldHeaderText, ...classes.showTableOrWineNumberSwitch }}>
                          {M.get('menuItem.showTableOrWineNumber')}{' '}
                          <Tooltip title={M.get('wineLists.showTableOrWineNumberTooltip')}>
                            <HelpOutlineIcon sx={classes.fieldHeaderIcon} />
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </FormControl>
                </Grid>
                {wineListsStore.getSelectedWines.length > 0 && (
                  <Grid
                    size={{ md: 12, lg: 7 }}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: { md: 'flex-start', lg: 'flex-end' },
                      gap: 2,
                      alignItems: { xs: 'stretch', sm: 'center' },
                    }}>
                    <FormControl size="small" variant="standard" sx={{ minWidth: 120 }}>
                      <InputLabel
                        id="is-table-or-wine-label"
                        sx={{
                          fontSize: '0.875rem',
                          fontFamily: 'unset',
                        }}>
                        {M.get('menuItem.setAllLocation')}
                      </InputLabel>
                      <Select
                        id="is-table-or-wine"
                        name="isTableOrWineAll"
                        value={setAllTableOrWineNumber}
                        label={M.get('menuItem.setAllLocation')}
                        labelId="is-table-or-wine-label"
                        disabled={!data.showTableOrWineNumber || disabled}
                        onChange={e => {
                          setSetAllTableOrWineNumber(e.target.value);
                        }}
                        sx={{
                          fontSize: '0.875rem',
                          fontFamily: 'unset',
                        }}>
                        <MenuItem
                          value=""
                          sx={{
                            fontSize: '0.875rem',
                            fontFamily: 'unset',
                          }}>
                          <em>{M.get('menuItem.tableOrWineNumberOptions.notSelected')}</em>
                        </MenuItem>
                        {tableOrWineNumberOptions.map(option => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                              fontSize: '0.875rem',
                              fontFamily: 'unset',
                            }}>
                            {option.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => wineListsStore.handleApplyToAll('isTableOrWine', setAllTableOrWineNumber)}
                      startIcon={<DoneAllIcon />}
                      disabled={!data.showTableOrWineNumber || disabled}
                      sx={classes.applyToAllButton}>
                      {M.get('wineLists.applyToAll')}
                    </Button>

                    <FormControl>
                      <TextField
                        size="small"
                        variant="standard"
                        id="tableOrWineNumberAll"
                        name="tableOrWineNumberAll"
                        value={tableOrWineNumberAll}
                        disabled={!data.showTableOrWineNumber || disabled}
                        label={M.get('menuItem.setAllNumber')}
                        onChange={e => {
                          setTableOrWineNumberAll(e.target.value);
                        }}
                        type="text"
                        sx={{
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                            fontFamily: 'unset',
                          },
                        }}
                        slotProps={{
                          htmlInput: { maxLength: 14 },
                          input: {
                            sx: {
                              fontSize: '0.875rem',
                              fontFamily: 'unset',
                              width: '120px',
                            },
                          },
                        }}
                      />
                    </FormControl>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => wineListsStore.handleApplyToAll('tableOrWineNumber', tableOrWineNumberAll)}
                      startIcon={<DoneAllIcon />}
                      disabled={!data.showTableOrWineNumber || disabled}
                      sx={classes.applyToAllButton}>
                      {M.get('wineLists.applyToAll')}
                    </Button>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <Box sx={classes.tableRoot}>
                    <Grid container sx={classes.container}>
                      <Box sx={classes.tabsContainer}>
                        <DraggableTable disabled={!data.showTableOrWineNumber || disabled} />
                      </Box>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </form>
        </Box>
        {/* Footer */}
        <Box sx={classes.pageFooter}>
          <Box>
            <Grid container sx={classes.footerPart}>
              <Button variant="text" color="primary" onClick={handleDiscard}>
                {M.get('actions.discard')}
              </Button>
              {!readonly && (
                <>
                  {!isCreate && (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitLoading || disabled}
                      onClick={handleOpenModalQR}>
                      {submitLoading ? <CircularProgress size={16} /> : M.get('wineLists.qr.openQrCode')}
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitLoading || disabled}
                    onClick={onSubmit}>
                    {submitLoading ? (
                      <CircularProgress size={16} />
                    ) : isCreate ? (
                      M.get('wineLists.qr.generateQrCode')
                    ) : (
                      M.get('actions.save')
                    )}
                  </Button>
                </>
              )}
            </Grid>
          </Box>
        </Box>
      </Box>
    </>
  );
});

WineListCreate.propTypes = {
  isEdit: PropTypes.bool,
  isCreate: PropTypes.bool,
};

export default WineListCreateWrapper;
